import type { CanvasNode, Connection } from '../types';
import { keepList, sameJson } from './unchanged';
import { whatToSave, type SavedWorkspace, type WorkspaceContents } from './unsavedNodes';

/**
 * Merging an update from the cloud into the open canvas. Replacing the canvas outright
 * loses whatever the user did since the last save (nodes just pasted or added, a
 * connection just drawn) whenever an update arrives first: the workspace's first sync
 * after it opens, or a collaborator's edit. Instead, three versions are compared: the
 * canvas now, the update, and the ids the canvas had when it last matched the cloud
 * (`synced`). What changed here since then is kept; everything else follows the cloud.
 *
 * An update holds the whole workspace, so most of it is what the canvas already has. A node
 * (or the whole list) that is the same keeps its object: nothing is rendered for an update
 * that changes nothing, such as the first one after a workspace opens.
 */

/** When the node's document was last written: every save stamps the node documents it writes. */
const stampOf = (node: CanvasNode): number | undefined => (node as CanvasNode & { lastModifiedAt?: number }).lastModifiedAt;

/**
 * Whether a value on the canvas is what the cloud holds for it. The cloud does not hold
 * everything as it is here: a key set to undefined is left out, and media still held inline
 * or as a blob (data:, blob:) is stored as an empty string.
 */
const sameAsCloud = (mine: unknown, theirs: unknown): boolean => {
    if (mine === theirs) return true;
    if (mine === undefined || mine === null) return theirs === undefined || theirs === null;
    if (typeof mine === 'string') return theirs === '' && (mine.startsWith('data:') || mine.startsWith('blob:'));
    if (typeof mine !== 'object' || typeof theirs !== 'object' || theirs === null) return false;
    if (Array.isArray(mine) !== Array.isArray(theirs)) return false;
    if (Array.isArray(mine)) {
        const other = theirs as unknown[];
        if (mine.length !== other.length) return false;
        for (let i = 0; i < mine.length; i++) {
            if (!sameAsCloud(mine[i], other[i])) return false;
        }
        return true;
    }
    const a = mine as Record<string, unknown>;
    const b = theirs as Record<string, unknown>;
    for (const key of Object.keys(a)) {
        if (!sameAsCloud(a[key], b[key])) return false;
    }
    for (const key of Object.keys(b)) {
        if (!(key in a) && b[key] !== undefined && b[key] !== null) return false;
    }
    return true;
};

/** Whether the node on the canvas is the one the cloud holds. When it was written does not count. */
export const sameNode = (mine: CanvasNode, theirs: CanvasNode): boolean => {
    if (mine === theirs) return true;
    if (mine.id !== theirs.id || mine.type !== theirs.type) return false;
    if (!sameAsCloud(mine.position, theirs.position) || !sameAsCloud(mine.size, theirs.size)) return false;
    const a = (mine.data || {}) as unknown as Record<string, unknown>;
    const b = (theirs.data || {}) as unknown as Record<string, unknown>;
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
        // A generation running here reads as idle in a copy that came back from the cloud (see rehydrateNodes)
        if (key === 'generationStatus' && a[key] === 'generating' && b[key] === 'idle') continue;
        if (!sameAsCloud(a[key], b[key])) return false;
    }
    return true;
};

/**
 * Whether the cloud's copy of a node is no news to this canvas: it is the copy the canvas's
 * own was taken from (nothing was written since, or only a save of this session that has not
 * come back yet), or it was written by a save of this session (`ownStamps`). Then the canvas
 * has that or something newer.
 */
const writtenHere = (mine: CanvasNode, theirs: CanvasNode, ownStamps: ReadonlySet<number>): boolean => {
    const stamp = stampOf(theirs);
    return stamp !== undefined && (stamp === stampOf(mine) || ownStamps.has(stamp));
};

const NO_STAMPS: ReadonlySet<number> = new Set<number>();

/**
 * Nodes after a cloud update:
 * - a node in the update follows the update, unless it has unsaved changes here (`keepLocal`),
 *   or was deleted here since the last sync;
 * - a node only here stays when it was added here since the last sync, or has unsaved
 *   changes; otherwise it was deleted elsewhere and goes.
 * A node the update does not change keeps its object, and `local` itself is returned when
 * the update changes no node at all.
 *
 * `ownStamps`: the stamps of this session's saves of this workspace. A copy carrying one of
 * them, or the stamp the canvas's copy has, is this session's own work coming back (possibly
 * an older state of it): the canvas keeps what it has.
 */
export const mergeRemoteNodes = (
    local: CanvasNode[],
    remote: CanvasNode[],
    synced: Set<string>,
    keepLocal: Set<string>,
    ownStamps: ReadonlySet<number> = NO_STAMPS,
): CanvasNode[] => {
    const localById = new Map(local.map(node => [node.id, node]));
    const remoteIds = new Set(remote.map(node => node.id));
    const merged: CanvasNode[] = [];
    for (const node of remote) {
        const mine = localById.get(node.id);
        if (!mine) {
            if (synced.has(node.id)) continue; // deleted here, and the cloud has not caught up yet
            merged.push(node); // added elsewhere
            continue;
        }
        const keepMine = keepLocal.has(node.id) || writtenHere(mine, node, ownStamps) || sameNode(mine, node);
        merged.push(keepMine ? mine : node);
    }
    for (const node of local) {
        if (remoteIds.has(node.id)) continue;
        if (!synced.has(node.id) || keepLocal.has(node.id)) merged.push(node); // added or changed here, not saved yet
    }
    return keepList(local, merged);
};

/**
 * The nodes a cloud update must not replace (`keepLocal` above): those marked as changed or
 * being saved, and those that are not the very objects last saved or loaded (`saved`; only
 * the marked ones when it is not known what is saved).
 */
export const nodesToKeep = (local: CanvasNode[], saved: CanvasNode[] | null, marked: Iterable<string>): Set<string> => {
    const keep = new Set(marked);
    if (saved) {
        const savedById = new Map(saved.map(node => [node.id, node]));
        for (const node of local) {
            if (savedById.get(node.id) !== node) keep.add(node.id);
        }
    }
    return keep;
};

/**
 * The nodes known to be saved once a cloud update is merged in, as the objects now on the
 * canvas (a node is unsaved when it is not the very object that was saved, see
 * utils/unsavedNodes.ts): what follows the cloud is saved as it is; a node kept from here
 * because of unsaved changes stays as it was last saved, so it still counts as changed; a
 * node that is only here is not saved.
 */
export const savedNodesAfterMerge = (
    merged: CanvasNode[],
    remote: CanvasNode[],
    saved: CanvasNode[] | null,
    keepLocal: Set<string>,
): CanvasNode[] => {
    const remoteIds = new Set(remote.map(node => node.id));
    const savedById = new Map((saved || []).map(node => [node.id, node]));
    const result: CanvasNode[] = [];
    for (const node of merged) {
        if (!remoteIds.has(node.id)) continue;
        if (!keepLocal.has(node.id)) {
            result.push(node);
            continue;
        }
        const before = savedById.get(node.id);
        if (before) result.push(before);
    }
    return result;
};

/**
 * The history after a cloud update. It only ever grows, one entry for each finished
 * generation, so the cloud's history is taken and the entries recorded here since the last
 * save (`saved`: the history as last saved or loaded) are kept after it.
 *
 * `merged` is `local` itself when nothing changes. `unsaved` tells whether it holds entries
 * the cloud does not have yet.
 */
export const mergeRemoteHistory = <T extends { id: string }>(
    local: T[],
    remote: T[],
    saved: T[] | null,
): { merged: T[]; unsaved: boolean } => {
    const remoteIds = new Set(remote.map(item => item.id));
    const savedIds = new Set((saved || []).map(item => item.id));
    const recordedHere = local.filter(item => !remoteIds.has(item.id) && !savedIds.has(item.id));
    const merged = recordedHere.length > 0 ? [...remote, ...recordedHere] : remote;
    return { merged: sameJson(local, merged) ? local : merged, unsaved: recordedHere.length > 0 };
};

/**
 * The connections of a cloud update that the canvas has taken in (the `synced` ones of the next
 * merge): those whose nodes are on the canvas. One left out because its node has not arrived
 * was never on the canvas, so it is not "removed here" when the node does arrive.
 */
export const syncedConnectionIds = (remote: Connection[], nodes: CanvasNode[]): Set<string> => {
    const nodeIds = new Set(nodes.map(node => node.id));
    return new Set(remote.filter(c => nodeIds.has(c.sourceId) && nodeIds.has(c.targetId)).map(c => c.id));
};

/**
 * The node ids the canvas and the cloud both have once a save has gone through (the `synced`
 * ones of the next merge): the nodes the save took and, when it wrote only some of them, those
 * a cloud update brought while it was under way and that are still on the canvas. A save of
 * every node clears from the cloud what it did not take: such a node is the canvas's own again.
 */
export const syncedNodeIdsAfterSave = (
    taken: CanvasNode[],
    wroteEveryNode: boolean,
    synced: ReadonlySet<string>,
    current: CanvasNode[],
): Set<string> => {
    const ids = new Set(taken.map(node => node.id));
    if (!wroteEveryNode) {
        for (const node of current) if (synced.has(node.id)) ids.add(node.id);
    }
    return ids;
};

const sameConnection = (a: Connection, b: Connection): boolean =>
    a === b || (a.id === b.id && a.sourceId === b.sourceId && a.targetId === b.targetId);

/**
 * Connections after a cloud update, by the same rule: ones drawn here since the last sync
 * stay, ones removed here stay removed, and the rest follow the cloud. Connections to nodes
 * that are gone are dropped. `local` itself is returned when nothing changes.
 *
 * `alsoOnCanvas`: ids of nodes that are on the canvas though `nodes` does not hold them (see
 * nodeIdsAddedWith).
 */
export const mergeRemoteConnections = (
    local: Connection[],
    remote: Connection[],
    synced: Set<string>,
    nodes: CanvasNode[],
    alsoOnCanvas: Iterable<string> = [],
): Connection[] => {
    const localIds = new Set(local.map(c => c.id));
    const remoteIds = new Set(remote.map(c => c.id));
    const nodeIds = new Set([...nodes.map(node => node.id), ...alsoOnCanvas]);
    const merged = [
        ...remote.filter(c => localIds.has(c.id) || !synced.has(c.id)),
        ...local.filter(c => !remoteIds.has(c.id) && !synced.has(c.id)),
    ];
    return keepList(local, merged.filter(c => nodeIds.has(c.sourceId) && nodeIds.has(c.targetId)), sameConnection);
};

/**
 * The nodes that connections added since `known` lead to and that were not on the canvas then
 * (`nodes`): nodes added together with them, by the same change.
 */
export const nodeIdsAddedWith = (connections: Connection[], known: Connection[], nodes: CanvasNode[]): Set<string> => {
    const knownIds = new Set(known.map(c => c.id));
    const nodeIds = new Set(nodes.map(node => node.id));
    const added = new Set<string>();
    for (const c of connections) {
        if (knownIds.has(c.id)) continue;
        if (!nodeIds.has(c.sourceId)) added.add(c.sourceId);
        if (!nodeIds.has(c.targetId)) added.add(c.targetId);
    }
    return added;
};

/**
 * How a list is set once a cloud update has been merged into it. The merge is worked out from
 * the list as it was last rendered (`startedFrom`), which lags a change made here from outside
 * an event (a generation that just finished, a pasted picture, a history entry just recorded):
 * such a change may still be on its way to the screen, and setting the list to `merged`
 * outright would lose it. So the list is set by this function of the list as it is when the
 * update is applied: `merged` when nothing came between, else the merge done again on that list.
 */
export const afterMerge = <T>(startedFrom: T[], merged: T[], mergeAgain: (latest: T[]) => T[]) =>
    (latest: T[]): T[] => (latest === startedFrom ? merged : mergeAgain(latest));

/**
 * A copy of a cloud workspace that was read from this device instead of the cloud: the
 * folder's file, or the device's own copy when the cloud could not be read. `savedAt`: when it
 * was saved, where that is known. `pending`: it was saved while the cloud did not take the
 * save, so it holds work the cloud does not have.
 */
export interface LocalCopy {
    savedAt: number | null;
    pending: boolean;
    /**
     * The nodes as they were read, for a copy whose nodes are not noted as saved (the device's
     * own copy: what the cloud holds of them is not known). They tell work done here since the
     * copy was opened from a copy that is merely not known to be in the cloud (changedHere).
     */
    loaded?: CanvasNode[];
}

/**
 * What a workspace file read from the folder says of itself as a copy. A file that says the
 * cloud took that very content (`cloudPending` false) is never the newer one, so its date is
 * not used: the date is this device's clock, the cloud's is the clock of whichever device
 * saved last, and the file is written some time after the cloud save it belongs to. The date
 * decides only for a file that does not say: one written before this was recorded, or by a
 * save that did not try the cloud.
 */
export const localCopyOfFile = (file: { savedAt?: number; cloudPending?: boolean }): LocalCopy => ({
    savedAt: file.cloudPending === false ? null : file.savedAt || null,
    pending: file.cloudPending === true,
});

/**
 * Whether the open workspace holds changes made here that are still to be saved: what an
 * auto-save waits for, and what makes a save on leaving one that carries changes.
 *
 * It is whatToSave's answer, with one exception. A workspace read from the device's own copy
 * is noted with its nodes not known to be saved (`saved.nodes` null), which whatToSave takes
 * for "write every node". Until the cloud has been heard (`localCopy` still set) that is not a
 * change made here: such a copy counts as changed only once it differs from what was read
 * (`localCopy.loaded`). Saved unasked, the copy would get today's date, or be marked as holding
 * work the cloud lacks, and would later be taken for newer than what other devices saved.
 */
export const changedHere = (
    saved: SavedWorkspace | null,
    localCopy: LocalCopy | null | undefined,
    current: WorkspaceContents,
    alsoChanged: Iterable<string> = [],
): boolean => {
    const asRead = saved && saved.nodes === null && localCopy?.loaded ? { ...saved, nodes: localCopy.loaded } : saved;
    return whatToSave(asRead, current, alsoChanged).save;
};

/**
 * Which of the workspace's two lists a save leaves as the cloud has them (saveProject's
 * `connectionsUnchanged` and `historyUnchanged`).
 *
 * While the canvas holds a copy read from this device and the cloud has not been heard
 * (`localCopy` set), the copy's connections and history may be older than the cloud's: a
 * list is sent only when it was changed here since the copy was read (it is no longer the
 * array noted in `saved`). Once the cloud has been heard the connections are always sent,
 * and the history unless the cloud is known to hold this very array (`cloudHistory`).
 *
 * A save that writes every node (`everyNode`) puts this device's whole copy in the cloud's
 * place, so both lists go with it: nodes written without the connections between them would
 * lose those connections at the next update.
 */
export const listsLeftToCloud = (
    localCopy: LocalCopy | null | undefined,
    saved: Pick<SavedWorkspace, 'connections' | 'history'> | null | undefined,
    cloudHistory: SavedWorkspace['history'] | null | undefined,
    current: Pick<WorkspaceContents, 'connections' | 'history'>,
    everyNode = false,
): { connectionsUnchanged: boolean; historyUnchanged: boolean } => {
    const copy = everyNode ? null : localCopy;
    return {
        connectionsUnchanged: !!copy && !!saved && current.connections === saved.connections,
        historyUnchanged: copy ? !!saved && current.history === saved.history : !!cloudHistory && current.history === cloudHistory,
    };
};

/**
 * A copy on this device is written a moment after the cloud save it belongs to. One saved
 * later than this after the cloud's last save was saved without the cloud.
 */
export const LOCAL_COPY_MARGIN_MS = 5000;

/**
 * Whether the copy on the canvas, read from this device, is newer than what the cloud holds.
 * Settled when the cloud is first heard after the workspace opened, from the cloud's own date
 * (`cloudLastSaved`: when the cloud's copy was last saved). The device's copy is the newer one
 * when it was saved while the cloud did not take the save, or more than the margin after the
 * cloud's last save (where its date counts: see localCopyOfFile). A session that may not
 * write (`mayWrite` false: view only) follows the cloud.
 */
export const localCopyIsNewer = (local: LocalCopy | null | undefined, cloudLastSaved: number, mayWrite: boolean): boolean =>
    !!local && mayWrite && (local.pending || (local.savedAt !== null && local.savedAt > (cloudLastSaved || 0) + LOCAL_COPY_MARGIN_MS));

/**
 * What a cloud update is merged against when the copy on the canvas is the newer one: nothing
 * of it counts as synced or saved. Every node on the canvas is marked as changed (`marks`), so
 * the update replaces none and all are saved. No node or connection counts as synced (the
 * caller empties those sets), so none is taken for one deleted elsewhere, and what only the
 * cloud has is added. No history entry counts as saved, so every one is kept. With the nodes
 * not known to be saved (`saved.nodes` null), the auto-save that follows writes every node.
 */
export const keepWholeLocalCopy = (localNodes: CanvasNode[], saved: SavedWorkspace): { marks: string[]; saved: SavedWorkspace } => ({
    marks: localNodes.map(node => node.id),
    saved: { ...saved, nodes: null, history: [] },
});
